import { createCssVariableString, includes, isValidPhoneNumber, useAlerts } from "best-common-react";
import { Location } from "react-router";
import qs from "query-string";
import React, { useEffect, useState } from "react";
import { isValidEmail } from "best-common-react";
import AlertConstants from "../constants/AlertConstants";
import fulfillmentTypes from "../constants/FulfillmentTypeConstants";
import UserAllotmentConstants from "../constants/UserAllotmentConstants";
import { useAuth } from "../contexts/AuthContext";
import { UserAllotmentCountDTO } from "../types/Allotment";
import { RequestTypeDesc } from "../types/RequestType";
import { FlattenedTicketRequest, TicketRequest, TicketRequestDTO } from "../types/TicketRequest";
import { hasAllotmentLeft } from "./AllotmentUtil";
import { isCCExpired } from "./CreditCardUtil";
import { RequestixLocationState } from "../types/Routing";
import { VenueAcceptedBilling } from "../types/Venue";
import { OPEN, IN_REVIEW, APPROVED, DENIED, CANCELLED } from "../constants/RequestTableConstants";

export const isChargeDept = (request: TicketRequest | TicketRequestDTO) =>
  request?.requestFulfillmentType?.requestFulfillmentTypeId === fulfillmentTypes.CHARGE_DEPT;

export const isChargeCard = (request: TicketRequest | TicketRequestDTO) =>
  request?.requestFulfillmentType?.requestFulfillmentTypeId === fulfillmentTypes.CHARGE_CARD;

export const isComp = (request: TicketRequest | TicketRequestDTO): boolean =>
  request?.requestFulfillmentType?.requestFulfillmentTypeId === fulfillmentTypes.COMP;

export const isBusiness = (type?: RequestTypeDesc): boolean => UserAllotmentConstants.BUSINESS === type?.toLowerCase();

export const isBusinessType = (ticketRequest: any): boolean => ticketRequest?.type === UserAllotmentConstants.BUSINESS;

export const isCompOnly = (ticketRequest: TicketRequest): boolean => ticketRequest?.requestType?.requestTypeId === 1;

export const isChargeIfNecessary = (ticketRequest: TicketRequest): boolean =>
  ticketRequest?.requestType?.requestTypeId === 2;

export const isChargeDepartment = (ticketRequest: TicketRequest): boolean =>
  ticketRequest?.requestType?.requestTypeId === 3;

export const useRequestValidation = (
  ticketRequest: FlattenedTicketRequest,
  allotment?: UserAllotmentCountDTO,
  phoneNumberRequired = false,
) => {
  const { isAdmin } = useAuth();
  const [isValid, setIsValid] = React.useState(false);

  React.useEffect(() => {
    if (!!ticketRequest) {
      /* Request On Behalf Of */
      const validRequester = ticketRequest.requester?.employeeUserId > -1;
      /* Project */
      const validProject = !isBusinessType(ticketRequest) || ticketRequest.projectId !== -1;
      /* Business */
      const validBusiness = !isBusinessType(ticketRequest) || !!ticketRequest.businessId;
      /* Pay By */
      const validPayBy = ticketRequest.requestType?.requestTypeId > -1;
      /* Quantity */
      const validQuantity = ticketRequest.quantity;
      /* Credit Card */
      const validCard = !isChargeIfNecessary(ticketRequest) || !!ticketRequest.userBilling;
      /* Recipient */
      const validRecepient = ticketRequest.recipientFirst?.length > 0 && ticketRequest.recipientLast?.length > 0;
      /* For Government Official */
      const validGovtOfficial =
        typeof ticketRequest.forGovernmentUse === "boolean" &&
        (!isBusinessType(ticketRequest) ||
          (ticketRequest.approvingSupervisor && ticketRequest.approvingSupervisor?.employeeUserId));
      /* Delivery Email */
      const validEmail = isValidEmail(ticketRequest.deliveryEmail);
      /* Remaining Allotment */
      const validAllotment = !isCompOnly(ticketRequest) || hasAllotmentLeft(allotment) || isAdmin;
      /** phone number required */
      const validPhoneNumber = !phoneNumberRequired || isValidPhoneNumber(ticketRequest.phoneNumber);
      setIsValid(
        !!(
          !!ticketRequest &&
          validRequester &&
          validProject &&
          validBusiness &&
          validPayBy &&
          validQuantity &&
          validCard &&
          validRecepient &&
          validGovtOfficial &&
          validEmail &&
          validAllotment &&
          validPhoneNumber
        ),
      );
    } else {
      setIsValid(false);
    }
  }, [ticketRequest, allotment, isAdmin]);

  return isValid;
};

export const useValidCreditCard = (ticketRequest: any, venueBillings: VenueAcceptedBilling[] = [], venue: any) => {
  const { addAlert } = useAlerts();
  const [validCard, setValidCard] = useState(true);

  useEffect(() => {
    if (
      ticketRequest?.requestType?.requestTypeId &&
      (ticketRequest.requestType?.requestTypeId === 1 || ticketRequest.requestType?.requestTypeId === 3)
    ) {
      setValidCard(true);
      return;
    }
    if (venueBillings?.length && ticketRequest?.userBilling?.ccType && venue) {
      const ccTypeId = ticketRequest.userBilling.ccType.ccTypeId;
      const match = venueBillings.filter((vb) => vb.creditCardType && vb.creditCardType.ccTypeId === ccTypeId);
      if (!match || !match.length) {
        const cardList = venueBillings.map((vb) => ` ${vb.creditCardType.ccTypeName}`);
        const errorMessage = `This type of card is not accepted by ${venue.name}. Accepted cards include${cardList}`;
        addAlert({ type: AlertConstants.TYPES.DANGER, text: errorMessage });
        setValidCard(false);
      } else {
        if (isCCExpired(ticketRequest.userBilling.ccExp)) {
          addAlert({
            type: AlertConstants.TYPES.DANGER,
            text: "Credit card is expired. Please select a valid credit card.",
          });
          setValidCard(false);
        } else {
          setValidCard(true);
        }
      }
    }
  }, [ticketRequest?.userBilling, venueBillings, ticketRequest?.requestType]);

  return validCard;
};

export const requestsFreeTextSearch = (requests: any[], query: string | undefined | null): any[] =>
  query && query.length
    ? requests.filter(
        (request) =>
          includes(request.gamePk, query) ||
          includes(request.status?.requestStateName, query) ||
          includes(request.requestFulfillmentType?.requestFulfillmentTypeName, query) ||
          includes(request.requester, query) ||
          includes(request.recipient, query) ||
          includes(request.department, query) ||
          includes(request.quantity, query) ||
          includes(request.maxPrice, query) ||
          includes(request.category, query) ||
          includes(request.payBy, query) ||
          includes(request.project, query) ||
          includes(request.business, query) ||
          includes(request.submitted, query) ||
          includes(request.comments, query) ||
          includes(request.supervisor, query) ||
          includes(request.statusName, query) ||
          includes(request.totalTicketCost, query) ||
          includes(request.homeTeam, query),
      )
    : requests;

export const requestsFreeTextFilter = (requests: object, query: string): object => {
  const results = {};
  if (requests) {
    Object.keys(requests).forEach((key) => {
      //@ts-ignore
      results[key] = requestsFreeTextSearch(requests[key], query);
    });
  }
  return results;
};

export const getStatusColor = (
  _requestId: number,
  requestStateId: number,
  activeRequest?: boolean,
): string | undefined => {
  if (activeRequest) {
    return createCssVariableString("coreColorsColoredSurfaceYellow");
  } else {
    switch (requestStateId) {
      case OPEN:
        return undefined;
      case IN_REVIEW:
        return createCssVariableString("coreColorsColoredSurfaceBlue");
      case APPROVED:
        return createCssVariableString("coreColorsColoredSurfaceGreen");
      case DENIED:
        return createCssVariableString("coreColorsColoredSurfaceRed");
      case CANCELLED:
        return createCssVariableString("color-on-surface-lowest");
      default:
        return "";
    }
  }
};

/** Admin request accordion header tint; uses `--color-surface-*-transparent` tokens (not full row colors). */
export const getStatusAccordionHeaderBackground = (
  _requestId: number,
  requestStateId: number,
  activeRequest?: boolean,
): string | undefined => {
  if (activeRequest) {
    return createCssVariableString("color-surface-yellow-transparent", true);
  }
  switch (requestStateId) {
    case OPEN:
      return undefined;
    case IN_REVIEW:
      return createCssVariableString("color-surface-blue-transparent", true);
    case APPROVED:
      return createCssVariableString("color-surface-green-transparent", true);
    case DENIED:
      return createCssVariableString("color-surface-red-transparent", true);
    case CANCELLED:
      return undefined;
    default:
      return undefined;
  }
};

export const removeRequestId = (location: Location<RequestixLocationState>): string => {
  let search = "?";
  if (location) {
    const params = qs.parse(location.search);
    Object.keys(params).forEach((key) => {
      if (key !== "requestId" && params[key]) {
        search += `&${key}=${params[key]}`;
      }
    });
  }
  return search;
};
